import React from "react";
import {
  BannerCardsStyle,
  EnginePreset,
  PreviewBanner,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/bannerCardsService";
import { shapePath } from "../TabShapeEditor";

/** A safe id for SVG url(#…) references (React ids contain colons). */
export const useSvgId = (prefix: string) => `${prefix}${React.useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

/** Outline of one banner box: the polygon shape, or a rounded rectangle. */
export const boxPath = (box: BannerCardsStyle["box"], w: number, h: number, inset = 0): string => {
  if (box.shape) return shapePath(box.shape, w, h, inset);
  const x0 = inset;
  const y0 = inset;
  const x1 = w - inset;
  const y1 = h - inset;
  const r = Math.max(0, Math.min(box.radius - inset, (x1 - x0) / 2, (y1 - y0) / 2));
  return (
    `M${x0 + r},${y0} H${x1 - r} Q${x1},${y0} ${x1},${y0 + r} V${y1 - r} Q${x1},${y1} ${x1 - r},${y1} ` +
    `H${x0 + r} Q${x0},${y1} ${x0},${y1 - r} V${y0 + r} Q${x0},${y0} ${x0 + r},${y0} Z`
  );
};

const truncate = (text: string, maxChars: number) =>
  text.length <= maxChars ? text : `${text.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;

/**
 * One banner box (photo clipped to the box shape, title, border) as an SVG
 * group at 0,0 with size w×h. Used on its own and inside the train coaches.
 */
export const BoxArt: React.FC<{
  banner: PreviewBanner;
  style: BannerCardsStyle;
  w: number;
  h: number;
}> = ({ banner, style, w, h }) => {
  const id = useSvgId("bx");
  const { box, title } = style;
  const outline = boxPath(box, w, h);
  const fadeStops =
    title.position === "top"
      ? [
          [0, 0.62],
          [0.62, 0],
        ]
      : title.position === "bottom"
        ? [
            [0.38, 0],
            [1, 0.62],
          ]
        : [
            [0, 0.3],
            [1, 0.3],
          ];
  const ty = title.position === "top" ? title.size + 6 : title.position === "bottom" ? h - 8 : h / 2 + title.size * 0.35;
  const maxChars = Math.max(3, Math.floor((w - 12) / (title.size * 0.6)));
  return (
    <g>
      <defs>
        <clipPath id={`${id}c`}>
          <path d={outline} />
        </clipPath>
        <linearGradient id={`${id}p`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={banner.gradient[0]} />
          <stop offset="1" stopColor={banner.gradient[1]} />
        </linearGradient>
        <linearGradient id={`${id}f`} x1="0" y1="0" x2="0" y2="1">
          {fadeStops.map(([o, a]) => (
            <stop key={o} offset={o} stopColor="#000" stopOpacity={a} />
          ))}
        </linearGradient>
      </defs>
      <g clipPath={`url(#${id}c)`}>
        <rect width={w} height={h} fill={`url(#${id}p)`} />
        {!banner.imageUrl && (
          <>
            <circle cx={w * 0.85} cy={h * 0.85} r={h * 0.45} fill="rgba(255,255,255,0.14)" />
            <circle cx={w * 0.12} cy={h * 1.05} r={h * 0.3} fill="rgba(255,255,255,0.1)" />
          </>
        )}
        {banner.imageUrl && (
          <image href={banner.imageUrl} width={w} height={h} preserveAspectRatio="xMidYMid slice" />
        )}
        {title.show && title.fade && <rect width={w} height={h} fill={`url(#${id}f)`} />}
        {title.show && banner.title && (
          <text
            x={w / 2}
            y={ty}
            textAnchor="middle"
            fill={title.color}
            fontSize={title.size}
            fontWeight={700}
            letterSpacing={0.2}
            fontFamily="Poppins, Inter, system-ui, sans-serif"
          >
            {truncate(banner.title, maxChars)}
          </text>
        )}
      </g>
      {box.borderWidth > 0 && (
        <path
          d={boxPath(box, w, h, box.borderWidth / 2)}
          fill="none"
          stroke={box.borderColor}
          strokeWidth={box.borderWidth}
        />
      )}
    </g>
  );
};

/** A spoked wheel; `angle` in degrees. */
export const Wheel: React.FC<{ cx: number; cy: number; r: number; color: string; hub: string; angle: number }> = ({
  cx,
  cy,
  r,
  color,
  hub,
  angle,
}) => (
  <g>
    <circle cx={cx} cy={cy} r={r} fill={color} />
    <circle cx={cx} cy={cy} r={r * 0.78} fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth={Math.max(0.6, r * 0.08)} />
    <g transform={`rotate(${angle.toFixed(1)} ${cx} ${cy})`}>
      {[0, 45, 90, 135].map((a) => (
        <line
          key={a}
          x1={cx - Math.cos((a * Math.PI) / 180) * r * 0.74}
          y1={cy - Math.sin((a * Math.PI) / 180) * r * 0.74}
          x2={cx + Math.cos((a * Math.PI) / 180) * r * 0.74}
          y2={cy + Math.sin((a * Math.PI) / 180) * r * 0.74}
          stroke="rgba(255,255,255,0.55)"
          strokeWidth={Math.max(0.6, r * 0.1)}
        />
      ))}
    </g>
    <circle cx={cx} cy={cy} r={r * 0.26} fill={hub} />
  </g>
);

/**
 * Engines are drawn in a 100-unit-long box facing left: the nose at x = 0,
 * the rail top at y = 0, everything above it negative. ENGINE_TOP is the
 * highest point (festival flag), used to size the scene.
 */
export const ENGINE_LENGTH = 100;
export const ENGINE_TOP = 86;

/** Where smoke leaves the chimney, or null for engines without one. */
export const CHIMNEY: Record<EnginePreset, [number, number] | null> = {
  steam: [24.5, -62],
  festival: [24.5, -62],
  toy: [27, -69],
  bullet: null,
};

const DARK = "#263238";
const GLASS = "#D6ECFB";

export const EngineArt: React.FC<{
  preset: EnginePreset;
  color: string;
  accent: string;
  wheels: { show: boolean; color: string };
  /** Wheel turn in degrees (already signed for facing left). */
  angle: number;
}> = ({ preset, color, accent, wheels, angle }) => {
  const wheel = (cx: number, r: number, hub = accent) =>
    wheels.show ? <Wheel key={cx} cx={cx} cy={-r} r={r} color={wheels.color} hub={hub} angle={angle} /> : null;
  const rad = (angle * Math.PI) / 180;

  if (preset === "bullet") {
    return (
      <g>
        {!wheels.show && <rect x={4} y={-12} width={96} height={11} rx={3} fill={DARK} />}
        {wheels.show && <rect x={6} y={-13} width={94} height={5} fill={DARK} />}
        {[22, 40, 70, 88].map((x) => wheel(x, 6))}
        <path d="M0,-13 C0,-30 18,-52 46,-56 L100,-56 L100,-10 L5,-10 Q0,-10 0,-13 Z" fill={color} />
        <path d="M2,-19 L100,-26 L100,-21 L3,-15 Z" fill={accent} />
        <path d="M13,-34 C19,-44 29,-50 41,-52 L43,-38 Z" fill={DARK} opacity={0.88} />
        {[54, 70, 86].map((x) => (
          <rect key={x} x={x} y={-49} width={11} height={11} rx={2} fill={GLASS} />
        ))}
        <ellipse cx={5} cy={-19} rx={3} ry={2} fill="#FFF59D" />
      </g>
    );
  }

  if (preset === "toy") {
    return (
      <g>
        <rect x={6} y={-17} width={94} height={7} rx={3.5} fill="#5D4037" />
        {!wheels.show && <rect x={8} y={-12} width={90} height={10} rx={5} fill={accent} />}
        <rect x={10} y={-46} width={56} height={32} rx={16} fill={color} />
        {[
          [30, -36],
          [44, -26],
          [54, -38],
        ].map(([x, y]) => (
          <circle key={x} cx={x} cy={y} r={2.6} fill="#FFFFFF" opacity={0.55} />
        ))}
        <circle cx={12} cy={-30} r={12} fill={accent} stroke={color} strokeWidth={2} />
        <circle cx={8} cy={-33} r={1.6} fill={DARK} />
        <circle cx={16} cy={-33} r={1.6} fill={DARK} />
        <path d="M7.5,-26.5 Q12,-22 16.5,-26.5" fill="none" stroke={DARK} strokeWidth={1.4} strokeLinecap="round" />
        <path d="M20,-46 L34,-46 L37,-62 L17,-62 Z" fill={accent} />
        <circle cx={27} cy={-65} r={4} fill="#FF7043" />
        <rect x={64} y={-66} width={34} height={52} rx={10} fill={accent} />
        <rect x={60} y={-72} width={42} height={8} rx={4} fill={color} />
        <circle cx={81} cy={-46} r={8} fill={GLASS} stroke={color} strokeWidth={2} />
        {wheel(26, 9, accent)}
        {wheel(50, 9, color)}
        {wheel(82, 9, accent)}
      </g>
    );
  }

  // steam and festival
  return (
    <g>
      {!wheels.show && <rect x={8} y={-16} width={90} height={14} rx={2} fill={DARK} />}
      <rect x={6} y={-17} width={94} height={6} fill={DARK} />
      <polygon points="1,-3 13,-3 13,-19 9,-19" fill={accent} />
      <rect x={12} y={-44} width={56} height={28} rx={10} fill={color} />
      <rect x={30} y={-44} width={3} height={28} fill={accent} />
      <rect x={50} y={-44} width={3} height={28} fill={accent} />
      <rect x={10} y={-42} width={8} height={24} rx={3} fill={DARK} />
      <circle cx={12} cy={-38} r={3} fill="#FFF59D" />
      <rect x={20} y={-58} width={9} height={15} fill={DARK} />
      <rect x={17} y={-62} width={15} height={5} rx={1.5} fill={DARK} />
      <path d="M38,-44 a6,6 0 0 1 12,0 Z" fill={accent} />
      <rect x={66} y={-64} width={32} height={48} rx={3} fill={color} />
      <rect x={62} y={-69} width={40} height={6} rx={2} fill={accent} />
      <rect x={72} y={-58} width={20} height={16} rx={2} fill={GLASS} stroke={accent} strokeWidth={1.5} />
      {wheel(30, 10)}
      {wheel(52, 10)}
      {wheel(84, 7)}
      {wheels.show && (
        <line
          x1={30 + Math.cos(rad) * 6}
          y1={-10 + Math.sin(rad) * 6}
          x2={52 + Math.cos(rad) * 6}
          y2={-10 + Math.sin(rad) * 6}
          stroke={accent}
          strokeWidth={2.5}
          strokeLinecap="round"
        />
      )}
      {preset === "festival" && (
        <g>
          <path d="M30,-45 Q48,-36 66,-45" fill="none" stroke="#2E7D32" strokeWidth={1} />
          {Array.from({ length: 7 }, (_, i) => {
            const t = i / 6;
            const x = 30 + t * 36;
            const y = -45 + 4 * t * (1 - t) * 4.5;
            return <circle key={i} cx={x} cy={y} r={2.4} fill={i % 2 ? "#FFC107" : "#FF6F00"} />;
          })}
          <line x1={92} y1={-69} x2={92} y2={-86} stroke={DARK} strokeWidth={1.4} />
          <polygon points="92,-86 104,-81.5 92,-77" fill="#E91E63" />
        </g>
      )}
    </g>
  );
};

/** Small picture of one preset for the engine picker. */
export const EngineThumb: React.FC<{ preset: EnginePreset; color: string; accent: string; wheelColor: string }> = ({
  preset,
  color,
  accent,
  wheelColor,
}) => (
  <svg viewBox={`-4 ${-ENGINE_TOP - 2} 112 ${ENGINE_TOP + 6}`} width={92} height={74} aria-hidden>
    <line x1={-4} y1={0.8} x2={108} y2={0.8} stroke="#B0BEC5" strokeWidth={1.6} />
    <EngineArt preset={preset} color={color} accent={accent} wheels={{ show: true, color: wheelColor }} angle={0} />
  </svg>
);
